import {JsonSchema, WidgetName} from './schema-form.types';
import {getValueFieldDisplay, IDSValueField} from '../../checks/bimCheck/components/ids/models/ids.model';

/**
 * Resolve which widget renders a schema property.
 *
 * `x-widget` (declared in the manifest schema) always wins; otherwise the widget
 * is inferred purely from the schema structure. This is the single, module-agnostic
 * place where the mapping lives — no per-module logic anywhere in the renderer.
 */
export function resolveWidget(schema: JsonSchema | null | undefined): WidgetName {
  // Explicit override always wins.
  const explicit = (schema ?? {})['x-widget'];
  if (explicit) return explicit as WidgetName;
  return structuralWidget(schema);
}

/**
 * Widget allein aus dem Schema-Aufbau — `x-widget` wird ignoriert.
 *
 * Braucht der Festwert-Zweig einer Bindung: dort steht in `x-widget` schon `value-binding`, das
 * eigentliche Eingabefeld muss aber weiterhin zu Typ und `enum` passen.
 */
export function structuralWidget(schema: JsonSchema | null | undefined): WidgetName {
  const s = schema ?? {};

  // Enum wins over the base type (e.g. integer + enum → dropdown).
  if (Array.isArray(s.enum) && s.enum.length > 0) return 'select';

  // Structural types before x-source. Arrays with a known option set (source or
  // enum) become a multi-select (Regelset pattern); free-form arrays become chips.
  if (s.type === 'array') {
    const items = s.items ?? {};
    if (items.type === 'object' || items.properties) return 'object-array';
    if (s['x-source'] || (Array.isArray(items.enum) && items.enum.length > 0)) return 'multiselect';
    return 'chips';
  }
  if (s.type === 'object' || s.properties) return 'object';

  // Scalar dynamic source → dropdown.
  if (s['x-source']) return 'select';

  if (s.type === 'boolean') return 'toggle';
  if (s.type === 'integer' || s.type === 'number') return 'number';
  return 'input';
}

/**
 * `x-visible-when: {property, equals}` bzw. `{property, in: [...]}` gegen die Werte, unter denen
 * die Bedingung steht (Geschwister im selben Eintrag, oder die Einstellungen des Knotens).
 */
export function matchesVisibleWhen(prop: JsonSchema | null | undefined, values: any): boolean {
  const condition = prop?.['x-visible-when'];
  if (!condition?.property) return true;
  const actual = values?.[condition.property];
  if (Array.isArray(condition.in)) return condition.in.includes(actual);
  return actual === condition.equals;
}

/**
 * Kartentitel aus einer `x-card-title-template`-Vorlage, z. B. „Entität: {entityName}" oder je
 * Facette verschieden (`{by: 'facette', templates: {...}}`) — wie in der Figma-Vorlage der
 * IFC-Bausteine („Eigenschaft: Pset_WallCommon.FireRating = REI90").
 *
 * `{feld}` wird durch den Eintragswert ersetzt (Enums als Label, Listen mit „, "). Leere Felder
 * hinterlassen keine verwaisten Trenner („Eigenschaft: .FireRating =" → „Eigenschaft: FireRating").
 * `null`, wenn es keine passende Vorlage gibt oder kein einziges Feld gefüllt ist — dann greift
 * der einfache `x-card-title`.
 */
export function formatCardTitle(spec: any, row: any, itemSchema: JsonSchema | null | undefined): string | null {
  if (!spec) return null;
  const template: string | undefined = spec.by
    ? spec.templates?.[row?.[spec.by]]
    : spec.template;
  if (!template) return null;

  let filled = 0;
  const text = template.replace(/\{(\w+)\}/g, (_match: string, key: string) => {
    const raw = row?.[key];
    const propSchema = itemSchema?.properties?.[key];
    if (isValueField(raw)) {
      // IDS-Wertfeld (x-widget: ids-value): so lesbar wie im IDS-Editor („F30, F90 +1", „≥ 300").
      const shown = getValueFieldDisplay(raw, '', 3);
      if (shown) filled++;
      return shown;
    }
    const parts = (Array.isArray(raw) ? raw : [raw])
      .filter(v => v !== null && v !== undefined && String(v).trim() !== '')
      .map(v => Array.isArray(propSchema?.enum) ? enumLabel(propSchema, v) : String(v).trim());
    if (parts.length) filled++;
    return parts.join(', ');
  });
  if (!filled) return null;
  return text
    .replace(/(^|:\s*)\.(?=\S)/g, '$1')
    .replace(/[\s=→:.,]+$/u, '')
    .trim();
}

/** Ein Objekt in der Form von `IDSValueField` (mindestens die Wert-Art) – der Wert von `x-widget: ids-value`. */
export function isValueField(value: unknown): value is IDSValueField {
  return !!value && typeof value === 'object' && !Array.isArray(value)
    && typeof (value as IDSValueField).dataType === 'string';
}

/** Display label for an enum value, honoring an optional `x-enumLabels` map. */
export function enumLabel(schema: JsonSchema | null | undefined, value: any): string {
  const labels = schema?.['x-enumLabels'];
  if (labels && labels[value] != null) return labels[value];
  return String(value);
}

/**
 * Normalisiert einen JSON-Schema-`type` (einzelner String oder Array, z.B. bei Wertevergleich
 * `['number','integer','boolean']`) auf Vergleichsgruppen für die Bindungs-Kompatibilität.
 * `integer`/`number` teilen sich eine Gruppe — wie im früheren Datenport-System, das dieselbe
 * Prüfung (string, number, integer, any) beim Ziehen einer Kante gegen Port-Farben lief. Kein
 * `type` ergibt ein leeres Set, das `isTypeCompatible` als Joker liest.
 */
export function typeGroups(schemaType: string | string[] | null | undefined): Set<string> {
  const raw = Array.isArray(schemaType) ? schemaType : schemaType ? [schemaType] : [];
  const groups = new Set<string>();
  for (const t of raw) groups.add(t === 'integer' ? 'number' : t);
  return groups;
}

/**
 * Ob eine Bindungs-Quelle zu einem Ziel-Feld passt — die aus dem früheren Datenport-System
 * wiederhergestellte Typ-Kompatibilitätsprüfung (string/number/boolean/object/array, `integer`
 * zählt als `number`), jetzt gegen den JSON-Schema-`type` statt gegen Port-Farben beim
 * Kantenziehen.
 *
 * Fehlt der `type` auf einer der beiden Seiten, gilt das als Joker (= kompatibel): manche Felder
 * legen ihren Datentyp bewusst nicht fest (z.B. condition_checker's `wert_a`/`wert_b` — der
 * Datentyp wird zur Laufzeit abgeleitet, nicht im Schema eingeschränkt). Ohne diesen Vorrang
 * würde die Prüfung genau die Module blockieren, die absichtlich jeden Typ annehmen.
 */
export function isTypeCompatible(
  targetType: string | string[] | null | undefined,
  sourceType: string | string[] | null | undefined,
): boolean {
  const target = typeGroups(targetType);
  const source = typeGroups(sourceType);
  if (target.size === 0 || source.size === 0) return true;
  for (const group of target) if (source.has(group)) return true;
  return false;
}
